import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { Toast } from "primereact/toast";
import Swal from "sweetalert2";
import CompanyLogo from "assets/studyGramLogo/studygram_logo.png";
import "./index.css";
import { signOut } from "firebase/auth";
import { auth } from "helper/config/firebase";
import { useNavigate } from "react-router-dom";
import RoutingPaths from "helper/routingPath";
import "react-toastify/dist/ReactToastify.css";

function Header({ navbarCallBack }) {
  const [userMenuVisible, setUserMenuVisible] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();
  const toast = useRef(null);
  const userMenuRef = useRef(null);
  
  const authSession = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("session"));
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (authSession) {
      setUserInfo(authSession);
    }
  }, [authSession]);

  const handleSignOutConfirm = useCallback(() => {
    Swal.fire({
      title: "Sign Out?",
      text: "Are you sure you want to sign out?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#667eea",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Sign Out",
      cancelButtonText: "Cancel",
      backdrop: true,
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        signOutApi();
      }
    });
  }, []);

  const signOutApi = useCallback(async () => {
    try {
      await signOut(auth);
      if (authSession) {
        localStorage.clear();
      }
      navigate(RoutingPaths?.login);
      window.location.reload();
    } catch (error) {
      const errorMessage = error.message || 'Sign out failed';
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 3000
      });
    }
  }, [authSession, navigate]);

  const toggleSidebar = useCallback(() => {
    navbarCallBack();
  }, [navbarCallBack]);

  const handleProfileClick = useCallback(() => {
    navigate(RoutingPaths.profile);
    setUserMenuVisible(false);
  }, [navigate]);

  const getUserInitials = useCallback((name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }, []);

  const menuItems = useMemo(() => [
    {
      template: () => (
        <div className="user-menu-header">
          <Avatar
            label={getUserInitials(userInfo?.name)}
            shape="circle"
            className="user-menu-header-avatar"
          />
          <div className="user-menu-header-info">
            <span className="user-menu-header-name">{userInfo?.name || 'User'}</span>
            {userInfo?.role && (
              <span className="user-menu-header-role">
                {userInfo.role.charAt(0).toUpperCase() + userInfo.role.slice(1)}
              </span>
            )}
          </div>
        </div>
      )
    },
    { separator: true },
    {
      label: 'Profile',
      icon: 'pi pi-user',
      command: handleProfileClick
    },
    { separator: true },
    {
      label: 'Sign Out',
      icon: 'pi pi-sign-out',
      command: handleSignOutConfirm,
      className: 'sign-out-item'
    }
  ], [handleProfileClick, handleSignOutConfirm, userInfo, getUserInitials]);

  return (
    <>
      <header className="header-container" style={{ backgroundColor: '#ffffff' }}>
        <nav className="navbar navbar-expand-lg main__navbar_div" style={{ backgroundColor: '#ffffff' }}>
          <div className="navbar-content">
            {/* Sidebar Toggle */}
            <div className="sidebar-toggle">
              <Button
                icon="pi pi-bars"
                onClick={toggleSidebar}
                className="sidebar-toggle-btn"
                aria-label="Toggle navigation menu"
                text
                rounded
              />
            </div>

            {/* Logo/Brand */}
            <div className="brand-section">
              <img
                src={CompanyLogo}
                alt="StudyGram Logo"
                className="brand-logo"
                onClick={() => navigate(RoutingPaths.home)}
              />
            </div>

            {/* User Profile Section */}
            <div className="user-section">
              <div className="user-info">
                {userInfo?.name && (
                  <span className="user-greeting">
                    Hi, {userInfo.name.split(' ')[0]}
                  </span>
                )}
                <Avatar
                  label={getUserInitials(userInfo?.name)}
                  className="user-avatar"
                  onClick={(e) => userMenuRef.current?.toggle(e)}
                  style={{ cursor: 'pointer' }}
                  shape="circle"
                />
              </div>
              
              <Menu
                model={menuItems}
                popup
                ref={userMenuRef}
                className="user-menu"
                popupAlignment="right"
              />
            </div>
          </div>
        </nav>
      </header>
      
      <Toast ref={toast} position="top-right" />
    </>
  );
}

export default Header;
